import {
  Box,
  LabraToolTip,
  PlanType,
  RadioButtons,
} from '@labrav/react-components'
import React, { useEffect, useMemo, useState } from 'react'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { useStyles } from './PrivateOfferDimensions.styles'
import { Product } from '../../../modules/productsListing/types'
import { KeyValue } from '../../../modules/privateOffer/privateOfferCreation/types'
import {
  Divider,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import { isEmpty } from 'lodash'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { AddUpdateButton } from '../../../common/components/AddUpdateButton'
import {
  Dimension,
  EntitlementOption,
  PriceTypes,
} from '../../../modules/privateOffer/types'
import { useSelector } from '../../../store'
import clsx from 'clsx'
import {
  getRandom,
  isLoading,
  LoadingTypes,
  preventEventForKeys,
} from '@labrav/common'
import { SubscriptionTable } from './SubscriptionTable'
import { DimensionRows } from './DimensionRows'
import { AdditionalDimensionsRow } from './AdditionalDimensionsRow'
import { CustomDimensionRow } from './CustomDimensionRow'
import {
  dimensionSelectionMapping,
  getAdditionalUsageDimensions,
  getCustomDimensionForSubscription,
  getCustomDimensions,
  getDimensionPrice,
  getOfferIds,
  getStandardDimensions,
  getStandardProductDimensions,
  subscriptionTableMapping,
} from './utils'

export type PrivateOfferDimensionsProps = {
  dimensions: Dimension[]
  pricesArray?: [keyof PriceTypes]
  entitlementOption?: EntitlementOption
  flexiblePaymentTerms: boolean
  product: Product
  isResale?: boolean
  dimensionsError: Record<string, unknown>[]
  handleChange: (data: KeyValue) => void
  handleUpdateDimensionRow: (
    index: number,
    key: string,
    type?: string
  ) => (data: KeyValue) => void
  deleteDimensionsRow: (index: number, key: string) => () => void
  addDimension: (key: string, type?: string) => void
  updateStandardDimensions: (index: number, dimension: Dimension) => void
  isCustom?: boolean
}

export const PrivateOfferDimensions = React.memo(
  ({
    product,
    pricesArray,
    dimensions,
    dimensionsError,
    entitlementOption,
    flexiblePaymentTerms,
    handleUpdateDimensionRow,
    deleteDimensionsRow,
    addDimension,
    handleChange,
    isResale = false,
    isCustom = false,
    updateStandardDimensions,
  }: PrivateOfferDimensionsProps) => {
    const { classes } = useStyles()
    const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value
      handleChange({ key: 'entitlementOption', value })
    }
    const isProductProfessionalServices =
      product.productType === PlanType.PROFESSIONAL_SERVICES
    const [randomKey, setRandomKey] = useState<number>(getRandom())
    useEffect(() => {
      setRandomKey(getRandom())
    }, [dimensions.length])
    const offerIds = useMemo(() => getOfferIds(dimensions), [dimensions])
    const updateId = (index: number) => (event: SelectChangeEvent) => {
      const data: KeyValue = {
        key: 'dimensionId',
        value: event.target.value as string,
      }
      const { valueDataType, price, freeTrial, id, ...dimension } =
        productDimensions.find(dimension => dimension.id === data.value) || {}
      price
      updateStandardDimensions(index, {
        ...dimension,
        price: null,
        dimensionId: id,
      } as unknown as Dimension)
    }
    const updateQuantity =
      (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const data: KeyValue = {
          key: event.target.id,
          value: event.target.value as string,
        }
        handleUpdateDimensionRow(index, 'dimensions')(data)
      }
    const addProductDimensions = (type?: string) => () => {
      addDimension('dimensions', type)
    }
    const loading = useSelector<boolean>(state =>
      isLoading(state.loading)(LoadingTypes.GENERAL)
    )
    const productDimensions = useMemo(
      () => getStandardProductDimensions(product),
      [product]
    )
    const disabled =
      loading ||
      dimensions.filter(d => d.type === 'standard').length ===
        productDimensions.length

    const updatePrice =
      (index: number, key: string) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        handleUpdateDimensionRow(
          index,
          'dimensions'
        )({ key: key, value: event.target.value as string })
      }
    const customObject = dimensions.map((dimension, index) => {
      const key = isResale ? 'subscriptionPrice' : 'price'
      const { value, errorText } = getCustomDimensionForSubscription(
        dimension,
        index,
        key,
        isResale,
        dimensionsError
      )
      return {
        name: dimension?.name || '',
        description: dimension?.labelDescription || '',
        rates: (
          <div key={`${randomKey}-${dimension?.name}`}>
            <TextField
              id="rate"
              variant="standard"
              defaultValue={value}
              className={classes.textField}
              required={true}
              onChange={updatePrice(index, key)}
              type="number"
              error={!isEmpty(errorText)}
              helperText={errorText}
              onKeyDown={preventEventForKeys(['+', '-', 'e'])}
              slotProps={{
                input: {
                  inputProps: { min: 0, 'data-testid': `price-${index}` },
                },
              }}
            />
          </div>
        ),
      }
    })
    const customDimensionsArray = getCustomDimensions(dimensions, isResale)
    const standardDimensionsArray = getStandardDimensions(dimensions)
    const additionalDimensionArray = getAdditionalUsageDimensions(dimensions)
    return (
      <Box
        heading={'Dimensions'}
        subHeading={'Product dimensions'}
        icon={<InfoOutlined className={classes.infoIcon} />}
        tooltipLabel={'User can choose the existing dimensions of the Product.'}
      >
        {product.productType !== PlanType.SAAS_SUBSCRIPTION ? (
          <>
            <div className={classes.root}>
              {entitlementOption &&
                product.productType !== PlanType.PROFESSIONAL_SERVICES && (
                  <>
                    <Typography className={classes.selectionHeading}>
                      Choose the entitlement type you want to offer
                    </Typography>
                    <RadioButtons
                      checkedValue={{
                        key: entitlementOption,
                        value: entitlementOption,
                      }}
                      handleChange={handleSelection}
                      radioButtonMappings={dimensionSelectionMapping}
                    />
                  </>
                )}
              {standardDimensionsArray.map(data => (
                <DimensionRows
                  index={data.index}
                  productDimensions={productDimensions}
                  isCustom={isCustom}
                  offerDimension={{
                    ...data.dimension,
                    price: getDimensionPrice(data.dimension, isResale),
                    id: data.dimension.dimensionId || '',
                  }}
                  pricesArray={isResale ? pricesArray : ['price']}
                  offerIds={offerIds}
                  updateId={updateId(data.index)}
                  deleteRow={deleteDimensionsRow(data.index, 'dimensions')}
                  updateQuantity={updateQuantity(data.index)}
                  isFreeTrial={false}
                  isFlexiblePayment={flexiblePaymentTerms}
                  errors={dimensionsError[data.index]}
                />
              ))}
              <div className={classes.container}>
                <AddUpdateButton
                  label={'Add dimension'}
                  disabled={disabled}
                  type={'add'}
                  icon={AddCircleOutlineIcon}
                  onClick={addProductDimensions('standard')}
                />
              </div>
            </div>
            {!isProductProfessionalServices ? (
              <div className={classes.dimensionsBody}>
                <div className={classes.subHeadingContainer}>
                  <Typography
                    className={classes.subHeading}
                    data-testid="dimensions-subheading"
                  >
                    Custom dimensions
                  </Typography>
                  <LabraToolTip
                    tooltipPlacement={'right'}
                    label={`Add any new dimensions that is not present in your listing and will be just for this specific ${
                      isResale ? 'resale authorization' : 'offer'
                    }.`}
                  >
                    <InfoOutlined className={classes.infoIcon} />
                  </LabraToolTip>
                </div>
                <Typography className={classes.labelText}>
                  You can create new custom dimensions only applicable for this{' '}
                  {isResale ? 'resale authorization' : 'offer'}.
                </Typography>
                <div
                  className={classes.customDimensionsRowsContainer}
                  data-testid="custom-dimension"
                >
                  {customDimensionsArray.map(data => (
                    <CustomDimensionRow
                      index={data.index}
                      dimensionsLength={customDimensionsArray.length}
                      customDimension={data.dimension}
                      updateDimensions={handleUpdateDimensionRow(
                        data.index,
                        'dimensions'
                      )}
                      deleteRow={deleteDimensionsRow(data.index, 'dimensions')}
                      key={data.index}
                      errors={dimensionsError[data.index]}
                      flexiblePaymentTerms={flexiblePaymentTerms}
                      isCustom={isCustom}
                      pricesArray={isResale ? pricesArray : ['price']}
                    />
                  ))}

                  <div className={classes.addButtonContainer}>
                    <AddUpdateButton
                      label={'Add a custom dimension'}
                      onClick={addProductDimensions('custom')}
                      disabled={false}
                      icon={AddCircleOutlineIcon}
                      type={'add-custom'}
                    />
                  </div>
                </div>
                {additionalDimensionArray.length > 0 && (
                  <div data-testid="additionalUsage-dimension-container">
                    <Divider className={classes.dimensionDivider} />
                    <div
                      className={clsx(
                        classes.subHeadingContainer,
                        classes.dimensionsMargin
                      )}
                    >
                      <Typography
                        className={classes.subHeading}
                        data-testid="dimensions-subheading"
                      >
                        Additional usage dimensions
                      </Typography>
                      <LabraToolTip
                        tooltipPlacement={'right'}
                        label={
                          'Set Pricing for additional usage dimension, which the buyer will be charged for outside the contract, incase they cross the limit.'
                        }
                      >
                        <InfoOutlined className={classes.infoIcon} />
                      </LabraToolTip>
                    </div>
                    <div className={classes.AUDContainer}>
                      {additionalDimensionArray.map(additionalDimension => (
                        <AdditionalDimensionsRow
                          additionalDimension={additionalDimension.dimension}
                          updateDimensions={handleUpdateDimensionRow(
                            additionalDimension.index,
                            'dimensions'
                          )}
                          key={additionalDimension.index}
                          errors={dimensionsError[additionalDimension.index]}
                          isResale={isResale}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <></>
            )}
          </>
        ) : (
          <SubscriptionTable
            mapping={subscriptionTableMapping}
            customObject={customObject}
          />
        )}
      </Box>
    )
  }
)
